import type { ErrorInfo } from 'react';

/**
 * エラーレポートの詳細情報
 */
interface ErrorReport {
  id: string;
  timestamp: number;
  error: {
    message: string;
    stack?: string;
    name: string;
  };
  context: {
    url: string;
    userAgent: string;
    userId?: string;
    sessionId?: string;
    componentStack?: string;
    errorBoundary?: string;
    breadcrumbs?: string[];
  };
  extra?: Record<string, unknown>;
}

/**
 * エラーログ設定
 */
interface ErrorLoggerConfig {
  maxBatchSize: number;
  maxRetries: number;
  retryDelay: number;
  endpoint: string;
  storageKey: string;
}

/**
 * エラーロギングサービス
 * バッチ処理、リトライ機能、オフライン対応を提供
 */
class ErrorLogger {
  private config: ErrorLoggerConfig = {
    maxBatchSize: 10,
    maxRetries: 3,
    retryDelay: 1000,
    endpoint: '/errors',
    storageKey: 'pending_error_reports',
  };

  private pendingReports: ErrorReport[] = [];
  private retryCount = 0;
  private isSubmitting = false;
  private submitTimer?: number;

  constructor(config?: Partial<ErrorLoggerConfig>) {
    if (config) {
      this.config = { ...this.config, ...config };
    }

    // ページ読み込み時に保存されたエラーレポートを復元
    this.loadPendingReports();

    // 定期的にバッチ送信をトリガー
    this.scheduleSubmission();

    // ページ離脱時に未送信のレポートを保存
    window.addEventListener('beforeunload', () => {
      this.savePendingReports();
    });
  }

  /**
   * エラーレポートを記録
   */
  logError(
    error: Error,
    errorInfo?: ErrorInfo,
    context?: Partial<ErrorReport['context']>,
    extra?: Record<string, unknown>,
  ): void {
    const report: ErrorReport = {
      id: this.generateId(),
      timestamp: Date.now(),
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name,
      },
      context: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId: this.getUserId() || undefined,
        sessionId: this.getSessionId(),
        componentStack: errorInfo?.componentStack || undefined,
        ...context,
      },
      extra,
    };

    // 重複チェック
    if (this.isDuplicateError(report)) {
      return;
    }

    this.pendingReports.push(report);

    // バッチサイズに達した場合は即座に送信
    if (this.pendingReports.length >= this.config.maxBatchSize) {
      this.submitReports();
    }
  }

  /**
   * 非同期アクションエラーをログ
   */
  logAsyncError(error: Error, actionName: string, context?: Record<string, unknown>): void {
    this.logError(
      error,
      undefined,
      {
        errorBoundary: 'AsyncAction',
        breadcrumbs: [`Action: ${actionName}`],
      },
      context,
    );
  }

  /**
   * APIエラーをログ
   */
  logApiError(error: Error, endpoint: string, method: string, status?: number): void {
    this.logError(
      error,
      undefined,
      {
        errorBoundary: 'APIClient',
        breadcrumbs: [`${method} ${endpoint}`, status ? `Status: ${status}` : ''].filter(Boolean),
      },
      { endpoint, method, status },
    );
  }

  /**
   * 強制的にすべての未送信レポートを送信
   */
  async flush(): Promise<void> {
    if (this.pendingReports.length === 0) return;

    await this.submitReports();
  }

  /**
   * 未送信レポートをクリア
   */
  clear(): void {
    this.pendingReports = [];
    this.savePendingReports();
  }

  /**
   * エラーレポート送信
   */
  private async submitReports(): Promise<void> {
    if (this.isSubmitting || this.pendingReports.length === 0) return;

    this.isSubmitting = true;
    const reportsToSubmit = [...this.pendingReports];

    try {
      const response = await fetch(this.config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reports: reportsToSubmit }),
      });

      if (response.ok) {
        // 送信成功：該当レポートを削除
        this.pendingReports = this.pendingReports.filter(
          (report) => !reportsToSubmit.some((submitted) => submitted.id === report.id),
        );
        this.retryCount = 0;
        this.savePendingReports();

        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ Error reports submitted: ${reportsToSubmit.length} reports`);
        }
      } else {
        throw new Error(`Failed to submit error reports: ${response.status}`);
      }
    } catch (error) {
      console.warn('Failed to submit error reports:', error);

      // リトライ処理
      if (this.retryCount < this.config.maxRetries) {
        this.retryCount++;
        const delay = this.config.retryDelay * 2 ** (this.retryCount - 1);

        setTimeout(() => {
          this.submitReports();
        }, delay);
      } else {
        // 最大リトライ回数に達した場合はローカルストレージに保存
        this.savePendingReports();
        this.retryCount = 0;

        if (process.env.NODE_ENV === 'development') {
          console.warn(`❌ Failed to submit error reports after ${this.config.maxRetries} retries`);
        }
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  /**
   * バッチ送信のスケジューリング
   */
  private scheduleSubmission(): void {
    if (this.submitTimer) {
      clearTimeout(this.submitTimer);
    }

    this.submitTimer = window.setTimeout(() => {
      if (this.pendingReports.length > 0) {
        this.submitReports();
      }
      this.scheduleSubmission();
    }, 5000); // 5秒ごとにチェック
  }

  /**
   * 重複エラーのチェック
   */
  private isDuplicateError(newReport: ErrorReport): boolean {
    const threshold = 60000; // 1分以内の重複をチェック
    const now = Date.now();

    return this.pendingReports.some((report) => {
      return (
        report.error.message === newReport.error.message &&
        report.error.name === newReport.error.name &&
        report.context.url === newReport.context.url &&
        now - report.timestamp < threshold
      );
    });
  }

  /**
   * ユニークIDの生成
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * ユーザーIDの取得（AuthContextから）
   */
  private getUserId(): string | undefined {
    // TODO: AuthContextからユーザーIDを取得
    return undefined;
  }

  /**
   * セッションIDの取得
   */
  private getSessionId(): string | undefined {
    // セッションストレージからセッションIDを取得、なければ生成
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = this.generateId();
      sessionStorage.setItem('session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * 未送信レポートをローカルストレージに保存
   */
  private savePendingReports(): void {
    try {
      localStorage.setItem(this.config.storageKey, JSON.stringify(this.pendingReports));
    } catch (error) {
      console.warn('Failed to save pending error reports to localStorage:', error);
    }
  }

  /**
   * ローカルストレージから未送信レポートを読み込み
   */
  private loadPendingReports(): void {
    try {
      const stored = localStorage.getItem(this.config.storageKey);
      if (stored) {
        const reports = JSON.parse(stored) as ErrorReport[];
        this.pendingReports = reports.filter((report) => {
          // 24時間以上古いレポートは破棄
          return Date.now() - report.timestamp < 24 * 60 * 60 * 1000;
        });

        // 読み込み後はストレージをクリア
        localStorage.removeItem(this.config.storageKey);
      }
    } catch (error) {
      console.warn('Failed to load pending error reports from localStorage:', error);
    }
  }
}

// シングルトンインスタンス
export const errorLogger = new ErrorLogger();

/**
 * React Error Boundary用のエラーログ関数
 */
export const logBoundaryError = (
  error: Error,
  errorInfo: ErrorInfo,
  boundaryName?: string,
): void => {
  errorLogger.logError(error, errorInfo, {
    errorBoundary: boundaryName || 'ErrorBoundary',
  });
};

/**
 * 非同期アクション用のエラーログ関数
 */
export const logAsyncActionError = (
  error: Error,
  actionName: string,
  context?: Record<string, unknown>,
): void => {
  errorLogger.logAsyncError(error, actionName, context);
};

/**
 * API呼び出し用のエラーログ関数
 */
export const logApiCallError = (
  error: Error,
  endpoint: string,
  method: string,
  status?: number,
): void => {
  errorLogger.logApiError(error, endpoint, method, status);
};
